import React, {useState, useEffect} from 'react';
import {withRouter} from "../../hoc/withRouter";
import {
    Breadcrumb, Col, Row, Input, Button, Checkbox, Form, Divider, Modal, Radio,
    Upload, InputNumber, Space, Steps, Progress, Select, DatePicker, TimePicker,
    Card, Tag, Tooltip, Typography
} from "antd";
import {
    UploadOutlined, UserOutlined, FileTextOutlined, CarOutlined,
    CalendarOutlined, CheckCircleOutlined, InfoCircleOutlined
} from "@ant-design/icons";
import FooterSection from "../../component/pageSection/FooterSection";
import PageHeader from "../../component/header/PageHeader";
import AreaProvinceSelect from "../../component/area/AreaProvinceSelect";
import AreaCitySelect from "../../component/area/AreaCitySelect";
import {appDefaultArea, appName} from "../../config/appDataConfig/appConfig";
import Cookies from "universal-cookie";
import {baseUrlCdnUpload} from "../../config/apiConfig/apiConfig";
import {bqBridgingService} from "../../services/BqBridging/BqBridgingService";
import "./formEvTaxi.css";
import dayjs, {Dayjs} from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

const {Step} = Steps;
const {Option} = Select;
const {TextArea} = Input;
const {Title, Text} = Typography;

// ─── Static Data ─────────────────────────────────────────────────────────────
const VEHICLE_LIST = [
    {
        key: "VFE34_4X2_AT_WI_BAT",
        name: "VFE 34 With Battery",
        otr: "Rp 411.860.000",
        otrNumber: 411860000,
        description: "",
        colors: ["jetblack", "neptunegrey", "desatsilver", "brahminywhite", "deepocean", "crimsonred", "vinfastblue"],
        imgBg: "#1a3c6e",
        imgUrl: "https://trimitra-catalog.s3.ap-southeast-1.amazonaws.com/amarta/product-variant/image/vinfastvfe34_4x2_at_wi_bat.jpg"
    },
    {
        key: "VF5_4X2_AT_WI_BAT",
        name: "VinFast VF 5",
        otr: "Rp 323.170.000",
        otrNumber: 323170000,
        description: "",
        colors:["neptunegrey", "brahminywhite", "crimsonred", "vinfastblue"],
        imgBg: "#2d6a4f",
        imgUrl: "https://trimitra-catalog.s3.ap-southeast-1.amazonaws.com/amarta/product-variant/image/vinfastvf5_4x2_at_wi_bat.jpg"
    },
    {
        key: "VFLIMO_WI_BAT",
        name: "VF LIMO GREEN",
        otr: "Rp 389.000.000",
        otrNumber: 389000000,
        description: "Taksi EV – solusi armada hijau",
        colors: ["jet black", "de sat silver", "infinity blanc"],
        imgBg: "#384967",
        imgUrl: "https://trimitra-catalog.s3.ap-southeast-1.amazonaws.com/amarta/product-variant/image/vinfastvflimo_wi_bat.jpg"
        // comingSoon: true
    }
];

const AREA_OPTIONS = [
    "JAKARTA", "JAWABARAT", "JAWATENGAH", "JAWATIMUR", "BALI",
    "SUMATERA UTARA", "SUMATERA SELATAN", "KALIMANTAN TIMUR",
    "SULAWESI SELATAN", "BANTEN"
];

const PREV_PLATFORM_OPTIONS = [
    {label: "Grab", value: "grab"},
    {label: "Gojek", value: "gojek"},
    {label: "InDriver", value: "indrive"},
    {label: "Maxim", value: "maxim"},
    {label: "LalaMove", value: "lalamove"},
    {label: "Green SM", value: "green_sm"},
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (val: number) =>
    "Rp " + val.toLocaleString("id-ID");

// Disable Sundays & must be >= H+2
const disableSurveyDate = (current: Dayjs) => {
    const minDate = dayjs().add(2, "day").startOf("day");
    if (!current) return false;
    return current.isBefore(minDate) || current.day() === 0;
};

// ─── Upload helper ────────────────────────────────────────────────────────────
const extractUploadUrl = (data: any): string => {
    if (data?.url_document) return data.url_document;
    if (data?.Location) return data.Location;
    if (typeof data === "string") return data;
    return "";
};

// ─── Component ────────────────────────────────────────────────────────────────
const FormGsmPlatform: React.FC<any> = () => {
    const cookies = new Cookies();

    // Area
    const [areaCode, setAreaCode] = useState<string>(
        cookies.get("_amc-a") || appDefaultArea
    );
    const [isDesktop, setIsDesktop] = useState(false);

    // Stepper
    const [currentStep, setCurrentStep] = useState(0);
    const TOTAL_STEPS = 5;

    // ── STEP 1 State ──────────────────────────────────────────────────────────
    const [submissionType, setSubmissionType] = useState<string | null>(null);
    const [referralId, setReferralId] = useState("");

    // Driver GSM Aktif
    const [sapId, setSapId] = useState("");
    const [gsmPhone, setGsmPhone] = useState("");
    const [gsmEmail, setGsmEmail] = useState("");
    const [operationalArea, setOperationalArea] = useState<string | null>(null);
    const [avgOnlineHours, setAvgOnlineHours] = useState<number | null>(null);
    const [avgMonthlyIncome, setAvgMonthlyIncome] = useState<number | null>(null);

    // Mitra Fleet Partner
    const [fleetName, setFleetName] = useState("");
    const [vehiclePlate, setVehiclePlate] = useState("");
    const [rentalDuration, setRentalDuration] = useState<number | null>(null);
    const [avgDailyDeposit, setAvgDailyDeposit] = useState<number | null>(null);

    // Pengajuan Baru
    const [hadDriverExperience, setHadDriverExperience] = useState<string | null>(null);
    const [previousPlatforms, setPreviousPlatforms] = useState<string[]>([]);
    const [drivingExperienceYears, setDrivingExperienceYears] = useState<number | null>(null);

    // ── STEP 2 State ──────────────────────────────────────────────────────────
    const [fullName, setFullName] = useState("");
    const [nik, setNik] = useState("");
    const [familyCardNumber, setFamilyCardNumber] = useState("");
    const [birthPlace, setBirthPlace] = useState("");
    const [birthDate, setBirthDate] = useState<Dayjs | null>(dayjs().subtract(21, "year"));
    const [maritalStatus, setMaritalStatus] = useState<string | null>(null);
    const [numberOfDependents, setNumberOfDependents] = useState<number | null>(null);

    const [primaryPhone, setPrimaryPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [domicileAddress, setDomicileAddress] = useState("");
    const [provinceCode, setProvinceCode] = useState("");
    const [provinceName, setProvinceName] = useState<string | null>(null);
    const [cityCode, setCityCode] = useState("");
    const [cityDistrict, setCityDistrict] = useState<string | null>(null);
    const [postalCode, setPostalCode] = useState("");
    const [homeOwnershipStatus, setHomeOwnershipStatus] = useState<string | null>(null);

    // Docs
    const [ktpPhotoUrl, setKtpPhotoUrl] = useState("");
    const [spouseKtpPhotoUrl, setSpouseKtpPhotoUrl] = useState("");
    const [simAPhotoUrl, setSimAPhotoUrl] = useState("");
    const [familyCardPhotoUrl, setFamilyCardPhotoUrl] = useState("");
    const [selfieWithKtpUrl, setSelfieWithKtpUrl] = useState("");
    const [bankStatementUrl, setBankStatementUrl] = useState("");

    // Upload loading flags
    const [uploading, setUploading] = useState<{[key: string]: boolean}>({});

    // ── STEP 3 State ──────────────────────────────────────────────────────────
    const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
    const [selectedColor, setSelectedColor] = useState<string | null>(null);
    const [downPaymentReady, setDownPaymentReady] = useState<number | null>(null);

    // ── STEP 4 State ──────────────────────────────────────────────────────────
    const [surveyDate, setSurveyDate] = useState<Dayjs | null>(null);
    const [surveyTime, setSurveyTime] = useState<Dayjs | null>(null);
    const [surveyLocation, setSurveyLocation] = useState("");

    const [interviewMethod, setInterviewMethod] = useState<string | null>(null);
    const [interviewDate, setInterviewDate] = useState<Dayjs | null>(null);
    const [interviewTime, setInterviewTime] = useState<Dayjs | null>(null);

    // ── STEP 5 State ──────────────────────────────────────────────────────────
    const [agreeBI, setAgreeBI] = useState(false);
    const [agreeData, setAgreeData] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Resize ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!cookies.get("_amc-a")) cookies.set("_amc-a", appDefaultArea);
        const handleResize = () => setIsDesktop(window.innerWidth >= 992);
        handleResize();
        window.addEventListener("resize", handleResize);
        setTimeout(() => window.scrollTo({top: 0, behavior: "smooth"}), 10);
        return () => window.removeEventListener("resize", handleResize);
        // eslint-disable-next-line
    }, []);

    const onAreaChange = (data: any) => {
        cookies.set("_amc-a", data.city_group);
        setAreaCode(data.city_group);
    };

    // ── Upload config ─────────────────────────────────────────────────────────
    const UPLOAD_DOC_TYPES: Record<string, string> = {
        ktp:           "gsm_platform_ktp",
        kk:            "gsm_platform_kk",
        ktp_pasangan:  "gsm_platform_ktp_pasangan",
        sim_a:         "gsm_platform_sim_a",
        selfie:        "gsm_platform_selfie_ktp",
        rekening_koran:"gsm_platform_rekening_koran",
    };

    const buildUploadProps = (field: string, onSuccess: (url: string) => void) => ({
        method: "POST" as const,
        name: "image",
        action: `${baseUrlCdnUpload}/upload-document/document-data`,
        maxCount: 1,
        accept: "image/*,.pdf",
        listType: "picture" as const,
        data: {
            document_name: (Math.random() + 1).toString(36).substring(7),
            company: "amarta",
            document_type: UPLOAD_DOC_TYPES[field] ?? `gsm_platform_${field}`,
        },
        onChange(info: any) {
            if (info.file.status === "uploading") {
                setUploading(prev => ({...prev, [field]: true}));
            }
            if (info.file.status === "done") {
                const url = extractUploadUrl(info.file.response?.data);
                onSuccess(url);
                setUploading(prev => ({...prev, [field]: false}));
            }
            if (info.file.status === "removed") {
                onSuccess("");
                setUploading(prev => ({...prev, [field]: false}));
            }
            if (info.file.status === "error") {
                Modal.error({title: "Upload Gagal", content: `Gagal upload ${field}`});
                setUploading(prev => ({...prev, [field]: false}));
            }
        }
    });

    // ── Validation per step ───────────────────────────────────────────────────
    const validateStep1 = () => {
        const errors: string[] = [];
        if (!submissionType) { errors.push("Jenis Pengajuan wajib dipilih"); }

        if (submissionType === "driver_gsm_aktif") {
            if (!sapId || sapId.length < 10) errors.push("SAP ID minimal 10 karakter");
            if (!gsmPhone || gsmPhone.length < 10) errors.push("No HP terdaftar di GSM wajib diisi");
            if (!operationalArea) errors.push("Area Operasional wajib dipilih");
            if (!avgMonthlyIncome || avgMonthlyIncome <= 0) errors.push("Rata-rata Income Bulanan harus > 0");
        }
        if (submissionType === "mitra_fleet_partner") {
            const platRegex = /^[A-Z]{1,2}\s\d{1,4}\s[A-Z]{1,3}$/i;
            if (!fleetName) errors.push("Nama Fleet Partner wajib diisi");
            if (!vehiclePlate) errors.push("Plat Nomor wajib diisi");
            else if (!platRegex.test(vehiclePlate.trim())) errors.push("Format Plat Nomor tidak valid (contoh: B 1234 XYZ)");
            if (!rentalDuration || rentalDuration <= 0) errors.push("Durasi Sewa wajib diisi");
            if (!avgDailyDeposit || avgDailyDeposit <= 0) errors.push("Rata-rata Setoran Harian harus > 0");
        }
        if (submissionType === "pengajuan_baru") {
            if (!hadDriverExperience) errors.push("Pertanyaan riwayat driver online wajib dijawab");
            if (drivingExperienceYears === null || drivingExperienceYears < 0) errors.push("Pengalaman Mengemudi wajib diisi");
        }
        return errors;
    };

    const validateStep2 = () => {
        const errors: string[] = [];
        if (!fullName) errors.push("Nama Lengkap wajib diisi");
        if (!nik || nik.length !== 16 || !/^\d+$/.test(nik)) errors.push("NIK harus 16 digit angka");
        if (!familyCardNumber || familyCardNumber.length !== 16 || !/^\d+$/.test(familyCardNumber)) errors.push("No KK harus 16 digit angka");
        if (!birthPlace) errors.push("Tempat Lahir wajib diisi");
        if (!birthDate) {
            errors.push("Tanggal Lahir wajib diisi");
        } else {
            const age = dayjs().diff(birthDate, "year");
            if (age < 21) errors.push("Usia minimal 21 tahun");
        }
        if (!maritalStatus) errors.push("Status Pernikahan wajib dipilih");
        if (!primaryPhone || primaryPhone.length < 10) errors.push("No HP Utama minimal 10 digit");
        if (!domicileAddress) errors.push("Alamat Domisili wajib diisi");
        if (!provinceName) errors.push("Provinsi wajib dipilih");
        if (!cityDistrict) errors.push("Kota/Kabupaten wajib dipilih");
        if (!homeOwnershipStatus) errors.push("Status Rumah wajib dipilih");
        if (!ktpPhotoUrl) errors.push("Foto KTP wajib diupload");
        if (!simAPhotoUrl) errors.push("Foto SIM A wajib diupload");
        if (!familyCardPhotoUrl) errors.push("Foto KK wajib diupload");
        if (!selfieWithKtpUrl) errors.push("Foto Selfie dengan KTP wajib diupload");
        return errors;
    };

    const validateStep3 = () => {
        const errors: string[] = [];
        if (!selectedVehicle) errors.push("Unit kendaraan wajib dipilih");
        if (!selectedColor) errors.push("Warna kendaraan wajib dipilih");
        if (!downPaymentReady || downPaymentReady <= 0) errors.push("Jumlah Uang Muka wajib diisi");
        return errors;
    };

    const validateStep4 = () => {
        const errors: string[] = [];
        if (!surveyDate) errors.push("Tanggal Survey wajib dipilih");
        if (!surveyTime) errors.push("Jam Survey wajib dipilih");
        if (!surveyLocation) errors.push("Lokasi Survey wajib diisi");
        if (!interviewMethod) errors.push("Metode Interview wajib dipilih");
        if (!interviewDate) errors.push("Tanggal Interview wajib dipilih");
        if (!interviewTime) errors.push("Jam Interview wajib dipilih");
        return errors;
    };

    const validateStep5 = () => {
        const errors: string[] = [];
        if (!agreeBI) errors.push("Persetujuan BI Checking wajib dicentang");
        if (!agreeData) errors.push("Persetujuan kebenaran data wajib dicentang");
        return errors;
    };

    const showErrors = (errors: string[]) => {
        Modal.error({
            title: "Mohon lengkapi data berikut",
            content: (
                <div>
                    {errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
            )
        });
    };

    const handleNext = () => {
        let errors: string[] = [];
        if (currentStep === 0) errors = validateStep1();
        if (currentStep === 1) errors = validateStep2();
        if (currentStep === 2) errors = validateStep3();
        if (currentStep === 3) errors = validateStep4();
        if (errors.length > 0) { showErrors(errors); return; }
        setCurrentStep(prev => prev + 1);
        window.scrollTo({top: 0, behavior: "smooth"});
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
        window.scrollTo({top: 0, behavior: "smooth"});
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const errors = validateStep5();
        if (errors.length > 0) { showErrors(errors); return; }

        const vehicleData = VEHICLE_LIST.find(v => v.key === selectedVehicle);

        const payload: any = {
            submission_type: submissionType,
            referral_id: referralId || null,

            // Conditional Step 1
            ...(submissionType === "driver_gsm_aktif" && {
                gsm_driver_info: {
                    sap_id: sapId,
                    registered_phone: gsmPhone,
                    registered_email: gsmEmail || null,
                    operational_area: operationalArea,
                    avg_online_hours_per_day: avgOnlineHours,
                    avg_monthly_income: avgMonthlyIncome
                }
            }),
            ...(submissionType === "mitra_fleet_partner" && {
                fleet_partner_info: {
                    fleet_name: fleetName,
                    vehicle_plate: vehiclePlate?.toUpperCase(),
                    rental_duration_months: rentalDuration,
                    avg_daily_deposit: avgDailyDeposit
                }
            }),
            ...(submissionType === "pengajuan_baru" && {
                new_applicant_info: {
                    prev_driver_experience: hadDriverExperience === "ya",
                    prev_platforms: hadDriverExperience === "ya" ? previousPlatforms : [],
                    driving_experience_years: drivingExperienceYears
                }
            }),

            // Step 2 – KYC
            personal_info: {
                full_name: fullName?.toUpperCase(),
                nik: nik,
                no_kk: familyCardNumber,
                birth_place: birthPlace,
                birth_date: birthDate?.format("YYYY-MM-DD"),
                marital_status: maritalStatus,
                dependents: numberOfDependents
            },
            contact_info: {
                phone_number: primaryPhone,
                email: contactEmail || null,
                address: domicileAddress,
                province_code: provinceCode,
                province: provinceName,
                city_code: cityCode,
                city: cityDistrict,
                postal_code: postalCode || null,
                home_status: homeOwnershipStatus
            },
            documents: {
                ktp_url: ktpPhotoUrl,
                ktp_pasangan_url: spouseKtpPhotoUrl || null,
                sim_a_url: simAPhotoUrl,
                kk_url: familyCardPhotoUrl,
                selfie_ktp_url: selfieWithKtpUrl,
                rekening_koran_url: bankStatementUrl || null
            },

            // Step 3 – Unit
            vehicle_selection: {
                vehicle_key: selectedVehicle,
                vehicle_name: vehicleData?.name,
                vehicle_otr: vehicleData?.otr,
                selected_color: selectedColor,
                down_payment_ready: downPaymentReady
            },

            // Step 4 – Schedule
            schedule: {
                survey: {
                    date: surveyDate?.format("YYYY-MM-DD"),
                    time: surveyTime?.format("HH:mm"),
                    location: surveyLocation
                },
                interview: {
                    method: interviewMethod,
                    date: interviewDate?.format("YYYY-MM-DD"),
                    time: interviewTime?.format("HH:mm")
                }
            },

            submitted_at: new Date().toISOString(),
            area_code: areaCode
        };

        console.log("=== GSM PLATFORM FORM SUBMISSION ===");
        console.log(JSON.stringify(payload, null, 2));
        console.log("====================================");

        setIsSubmitting(true);
        try {
            await bqBridgingService.storeDataGsmPlatform(payload);
            Modal.success({
                title: "Pengajuan Berhasil Dikirim",
                content: (
                    <div>
                        <p>Data pengajuan Anda telah berhasil dikirim.</p>
                        <p>Tim kami akan menghubungi Anda untuk proses selanjutnya.</p>
                    </div>
                ),
                okText: "Tutup",
                onOk: () => {
                    window.location.reload();
                }
            });
        } catch (error: any) {
            Modal.error({
                title: "Gagal Mengirim Data",
                content: error.message || "Terjadi kesalahan. Silakan coba lagi."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const sectionTitle = {fontSize: "15px", color: "#384967", fontWeight: 600};
    const fieldStyle = {marginBottom: "14px"};
    const labelStyle: React.CSSProperties = {
        fontSize: "13px", color: "#555", marginBottom: "4px", display: "block"
    };
    const progressPercent = Math.round((currentStep / (TOTAL_STEPS - 1)) * 100);

    const selectedVehicleData = VEHICLE_LIST.find(v => v.key === selectedVehicle);

    // ────────────────────────────────────────────────────────────────────────
    // RENDER STEPS
    // ────────────────────────────────────────────────────────────────────────

    const renderStep1 = () => (
        <div>
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Jenis Pengajuan</span>
            </Divider>

            <div style={fieldStyle}>
                <label style={labelStyle}>Jenis Pengajuan *</label>
                <Radio.Group
                    value={submissionType}
                    onChange={e => { setSubmissionType(e.target.value); }}
                >
                    <Space direction="vertical">
                        <Radio value="pengajuan_baru">Pengajuan Baru (Non Driver)</Radio>
                        <Radio value="driver_gsm_aktif">Driver GSM Aktif</Radio>
                        <Radio value="mitra_fleet_partner">Mitra Fleet Partner</Radio>
                    </Space>
                </Radio.Group>
            </div>

            <div style={fieldStyle}>
                <label style={labelStyle}>Referral ID (opsional)</label>
                <Input
                    value={referralId}
                    onChange={e => setReferralId(e.target.value)}
                    placeholder="Masukkan Referral ID jika ada"
                    style={{maxWidth: 300}}
                />
            </div>

            {/* ── Driver GSM Aktif ── */}
            {submissionType === "driver_gsm_aktif" && (
                <>
                    <Divider orientation="left" plain>
                        <span style={sectionTitle}>Informasi Akun GSM</span>
                    </Divider>
                    <Row gutter={[12, 0]}>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>SAP ID * (min. 10 karakter)</label>
                                <Input
                                    value={sapId}
                                    onChange={e => {
                                        const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                                        setSapId(v);
                                    }}
                                    placeholder="Contoh: 1234567890"
                                    maxLength={10}
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>No HP Terdaftar di GSM *</label>
                                <Input
                                    value={gsmPhone}
                                    onChange={e => {
                                        const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 13);
                                        setGsmPhone(v);
                                    }}
                                    placeholder="08xxxxxxxxxx"
                                    maxLength={13}
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Email Terdaftar di GSM (opsional)</label>
                                <Input
                                    value={gsmEmail}
                                    onChange={e => setGsmEmail(e.target.value)}
                                    placeholder="email@domain.com"
                                    type="email"
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Area Operasional *</label>
                                <Select
                                    value={operationalArea}
                                    onChange={v => setOperationalArea(v)}
                                    placeholder="Pilih area"
                                    style={{width: "100%"}}
                                >
                                    {AREA_OPTIONS.map(a => (
                                        <Option key={a} value={a}>{a}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Rata-rata Online (Jam / Hari)</label>
                                <InputNumber
                                    value={avgOnlineHours}
                                    onChange={v => setAvgOnlineHours(v)}
                                    min={0} max={24}
                                    placeholder="0"
                                    style={{width: "100%"}}
                                    addonAfter="jam"
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Rata-rata Income Bulanan *</label>
                                <InputNumber
                                    value={avgMonthlyIncome}
                                    onChange={v => setAvgMonthlyIncome(v)}
                                    min={0}
                                    placeholder="0"
                                    style={{width: "100%"}}
                                    prefix="Rp"
                                    formatter={v => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                    parser={v => Number(String(v).replace(/\./g, ""))}
                                />
                            </div>
                        </Col>
                    </Row>
                </>
            )}

            {/* ── Mitra Fleet Partner ── */}
            {submissionType === "mitra_fleet_partner" && (
                <>
                    <Divider orientation="left" plain>
                        <span style={sectionTitle}>Informasi Fleet Partner</span>
                    </Divider>
                    <Row gutter={[12, 0]}>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Nama Fleet Partner *</label>
                                <Input
                                    value={fleetName}
                                    onChange={e => setFleetName(e.target.value)}
                                    placeholder="Nama perusahaan / fleet"
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Plat Nomor Mobil Disewa * (contoh: B 1234 XYZ)</label>
                                <Input
                                    value={vehiclePlate}
                                    onChange={e => setVehiclePlate(e.target.value.toUpperCase())}
                                    placeholder="B 1234 XYZ"
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Durasi Sewa (bulan) *</label>
                                <InputNumber
                                    value={rentalDuration}
                                    onChange={v => setRentalDuration(v)}
                                    min={1}
                                    placeholder="0"
                                    style={{width: "100%"}}
                                    addonAfter="bulan"
                                />
                            </div>
                        </Col>
                        <Col xl={12} xs={24}>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Rata-rata Setoran Harian *</label>
                                <InputNumber
                                    value={avgDailyDeposit}
                                    onChange={v => setAvgDailyDeposit(v)}
                                    min={0}
                                    placeholder="0"
                                    style={{width: "100%"}}
                                    prefix="Rp"
                                    formatter={v => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                    parser={v => Number(String(v).replace(/\./g, ""))}
                                />
                            </div>
                        </Col>
                    </Row>
                </>
            )}

            {/* ── Pengajuan Baru ── */}
            {submissionType === "pengajuan_baru" && (
                <>
                    <Divider orientation="left" plain>
                        <span style={sectionTitle}>Riwayat Mengemudi</span>
                    </Divider>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Apakah pernah menjadi driver online? *</label>
                        <Radio.Group value={hadDriverExperience} onChange={e => setHadDriverExperience(e.target.value)}>
                            <Radio value="ya">Ya</Radio>
                            <Radio value="tidak">Tidak</Radio>
                        </Radio.Group>
                    </div>
                    {hadDriverExperience === "ya" && (
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Platform Sebelumnya</label>
                            <Checkbox.Group
                                options={PREV_PLATFORM_OPTIONS}
                                value={previousPlatforms}
                                onChange={vals => setPreviousPlatforms(vals as string[])}
                            />
                        </div>
                    )}
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Pengalaman Mengemudi (tahun) *</label>
                        <InputNumber
                            value={drivingExperienceYears}
                            onChange={v => setDrivingExperienceYears(v)}
                            min={0} max={50}
                            style={{width: 160}}
                            addonAfter="tahun"
                        />
                    </div>
                </>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div>
            {/* Data Pribadi */}
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Data Pribadi</span>
            </Divider>
            <Row gutter={[12, 0]}>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Nama Lengkap *</label>
                        <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Sesuai KTP"/>
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>NIK * (16 digit)</label>
                        <Input
                            value={nik}
                            onChange={e => setNik(e.target.value.replace(/[^0-9]/g, "").slice(0, 16))}
                            placeholder="16 digit NIK"
                            maxLength={16}
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>No Kartu Keluarga * (16 digit)</label>
                        <Input
                            value={familyCardNumber}
                            onChange={e => setFamilyCardNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 16))}
                            placeholder="16 digit No KK"
                            maxLength={16}
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Tempat Lahir *</label>
                        <Input value={birthPlace} onChange={e => setBirthPlace(e.target.value)} placeholder="Kota tempat lahir"/>
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Tanggal Lahir * (min. usia 21 tahun)</label>
                        <DatePicker
                            value={birthDate}
                            onChange={d => setBirthDate(d)}
                            format="DD-MM-YYYY"
                            style={{width: "100%"}}
                            defaultPickerValue={dayjs().subtract(21, "year")}
                            disabledDate={d => d && d.isAfter(dayjs().subtract(21, "year"))}
                            placeholder="DD-MM-YYYY"
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Status Pernikahan *</label>
                        <Select
                            value={maritalStatus}
                            onChange={v => setMaritalStatus(v)}
                            placeholder="Pilih status"
                            style={{width: "100%"}}
                        >
                            <Option value="Belum Menikah">Belum Menikah</Option>
                            <Option value="Menikah">Menikah</Option>
                            <Option value="Cerai Hidup">Cerai Hidup</Option>
                            <Option value="Cerai Mati">Cerai Mati</Option>
                        </Select>
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Jumlah Tanggungan</label>
                        <InputNumber
                            value={numberOfDependents}
                            onChange={v => setNumberOfDependents(v)}
                            min={0} max={20}
                            style={{width: "100%"}}
                            placeholder="0"
                        />
                    </div>
                </Col>
            </Row>

            {/* Kontak */}
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Kontak & Alamat</span>
            </Divider>
            <Row gutter={[12, 0]}>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>No HP Utama *</label>
                        <Input
                            value={primaryPhone}
                            onChange={e => setPrimaryPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 13))}
                            placeholder="08xxxxxxxxxx"
                            maxLength={13}
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Email</label>
                        <Input
                            value={contactEmail}
                            onChange={e => setContactEmail(e.target.value)}
                            type="email"
                            placeholder="email@domain.com"
                        />
                    </div>
                </Col>
                <Col xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Alamat Domisili *</label>
                        <TextArea
                            value={domicileAddress}
                            onChange={e => setDomicileAddress(e.target.value)}
                            rows={3}
                            placeholder="Alamat lengkap domisili saat ini"
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Provinsi *</label>
                        <AreaProvinceSelect
                            onAreaProvinceChangeProps={(data: any) => {
                                setProvinceCode(data.value);
                                setProvinceName(data.children);
                                setCityCode("");
                                setCityDistrict(null);
                            }}
                            country={"idn"}
                            provinceName={provinceName}
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Kota / Kabupaten *</label>
                        <AreaCitySelect
                            onAreaCityChangeProps={(data: any) => {
                                setCityCode(data.value);
                                setCityDistrict(data.children);
                            }}
                            provinceCode={provinceCode}
                            cityName={cityDistrict}
                        />
                    </div>
                </Col>
                <Col xl={12} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Kode Pos</label>
                        <Input
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                            placeholder="12345"
                            maxLength={5}
                            style={{width: 140}}
                        />
                    </div>
                </Col>
                <Col xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Status Rumah *</label>
                        <Radio.Group value={homeOwnershipStatus} onChange={e => setHomeOwnershipStatus(e.target.value)}>
                            <Radio value="Milik Sendiri">Milik Sendiri</Radio>
                            <Radio value="Kontrak">Kontrak / Sewa</Radio>
                            <Radio value="Keluarga">Rumah Keluarga</Radio>
                        </Radio.Group>
                    </div>
                </Col>
            </Row>

            {/* Upload Dokumen */}
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Upload Dokumen</span>
            </Divider>
            <Row gutter={[12, 16]}>
                {[
                    {key: "ktp",           label: "Foto KTP *",                              url: ktpPhotoUrl,        setter: setKtpPhotoUrl},
                    {key: "kk",            label: "Foto Kartu Keluarga *",                   url: familyCardPhotoUrl, setter: setFamilyCardPhotoUrl},
                    {key: "ktp_pasangan",  label: "Foto KTP Pasangan (jika menikah)",        url: spouseKtpPhotoUrl,  setter: setSpouseKtpPhotoUrl},
                    {key: "sim_a",         label: "Foto SIM A *",                            url: simAPhotoUrl,       setter: setSimAPhotoUrl},
                    {key: "selfie",        label: "Foto Selfie dengan KTP *",                url: selfieWithKtpUrl,   setter: setSelfieWithKtpUrl},
                    {key: "rekening_koran",label: "Rekening Koran 3 Bulan (opsional)",       url: bankStatementUrl,   setter: setBankStatementUrl},
                ].map(doc => (
                    <Col xl={12} xs={24} key={doc.key}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{doc.label}</label>
                            <br/>
                            <Upload {...buildUploadProps(doc.key, doc.setter)}>
                                <Button icon={<UploadOutlined/>} loading={uploading[doc.key]}>
                                    {doc.url ? "Ganti File" : "Upload File"}
                                </Button>
                            </Upload>
                            {doc.url && (
                                <div style={{marginTop: 10}}>
                                    <div style={{color: "green", marginBottom: 5}}>✓ {doc.label.replace(" *", "")} berhasil diunggah</div>
                                    <img src={doc.url} alt={doc.label} style={{maxWidth: "100%", maxHeight: "150px"}}/>
                                </div>
                            )}
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );

    const renderStep3 = () => (
        <div>
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Pilih Unit Kendaraan</span>
            </Divider>
            <p style={{color: "#777", marginBottom: 16, fontSize: 13}}>
                Area referensi OTR: <strong>{areaCode}</strong>
            </p>

            <Row gutter={[16, 16]}>
                {VEHICLE_LIST.map(v => {
                    const isSelected = selectedVehicle === v.key;
                    const isComingSoon = (v as any).comingSoon === true;
                    const imgUrl = (v as any).imgUrl as string | undefined;
                    return (
                        <Col xl={8} md={12} xs={24} key={v.key}>
                            <Card
                                hoverable
                                onClick={() => { setSelectedVehicle(v.key); setSelectedColor(null); }}
                                style={{
                                    border: isSelected ? "2.5px solid #384967" : "1.5px solid #e0e0e0",
                                    borderRadius: 10,
                                    cursor: "pointer",
                                    transition: "all 0.2s"
                                }}
                                bodyStyle={{padding: "16px"}}
                            >
                                <div
                                    style={{
                                        background: (v as any).imgBg ?? "#bdbdbd",
                                        borderRadius: 8,
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 12
                                    }}
                                >
                                    {imgUrl ? (
                                        <img
                                            src={imgUrl}
                                            alt={v.name}
                                            style={{width: "100%", height: "100%", objectFit: "cover"}}
                                        />
                                    ) : (
                                        <CarOutlined style={{fontSize: 48, color: "#fff"}}/>
                                    )}
                                </div>
                                <div style={{fontWeight: 700, fontSize: 15, color: "#384967"}}>{v.name}</div>
                                <div style={{color: "#777", fontSize: 12, marginBottom: 6}}>{v.description}</div>
                                {isComingSoon ? (
                                    <Tag color="orange" style={{marginTop: 4}}>🕐 OTR Coming Soon</Tag>
                                ) : (
                                    <div style={{fontWeight: 600, color: "#e03e3e", fontSize: 14}}>
                                        OTR {v.otr}
                                    </div>
                                )}
                                {isSelected && (
                                    <Tag color="#384967" style={{marginTop: 8}}>✓ Dipilih</Tag>
                                )}
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {selectedVehicle && (
                <Row gutter={[12, 0]} style={{marginTop: 24}}>
                    <Col xl={12} xs={24}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Pilih Warna *</label>
                            <Select
                                value={selectedColor}
                                onChange={v => setSelectedColor(v)}
                                placeholder="Pilih warna kendaraan"
                                style={{width: "100%"}}
                            >
                                {selectedVehicleData?.colors?.map(c => (
                                    <Option key={c} value={c}>{c}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col xl={12} xs={24}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Jumlah Uang Muka yang Sudah Siap *</label>
                            <InputNumber
                                value={downPaymentReady}
                                onChange={v => setDownPaymentReady(v)}
                                min={0}
                                style={{width: "100%"}}
                                prefix="Rp"
                                formatter={v => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                parser={v => Number(String(v).replace(/\./g, ""))}
                                placeholder="0"
                            />
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );

    const renderStep4 = () => (
        <div>
            {/* Survey Domisili */}
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Survey Domisili</span>
            </Divider>
            <p style={{color: "#888", fontSize: 12, marginBottom: 12}}>
                <InfoCircleOutlined/> Jadwal survey minimal H+2 dari hari ini. Hari Minggu tidak tersedia.
            </p>
            <Row gutter={[12, 0]}>
                <Col xl={8} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Tanggal Survey *</label>
                        <DatePicker
                            value={surveyDate}
                            onChange={d => setSurveyDate(d)}
                            disabledDate={disableSurveyDate}
                            format="DD-MM-YYYY"
                            style={{width: "100%"}}
                            placeholder="Pilih tanggal"
                        />
                    </div>
                </Col>
                <Col xl={8} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Jam Survey *</label>
                        <TimePicker
                            value={surveyTime}
                            onChange={t => setSurveyTime(t)}
                            format="HH:mm"
                            minuteStep={30}
                            style={{width: "100%"}}
                            placeholder="Pilih jam"
                            disabledTime={() => ({disabledHours: () => [0,1,2,3,4,5,6,7,20,21,22,23]})}
                        />
                    </div>
                </Col>
                <Col xl={8} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Lokasi Survey * &nbsp;
                            <Tooltip title="Masukkan alamat atau koordinat Google Maps">
                                <InfoCircleOutlined style={{color: "#888"}}/>
                            </Tooltip>
                        </label>
                        <TextArea
                            value={surveyLocation}
                            onChange={e => setSurveyLocation(e.target.value)}
                            rows={2}
                            placeholder="Alamat lengkap lokasi survey"
                        />
                    </div>
                </Col>
            </Row>

            {/* Online Interview */}
            <Divider orientation="left" plain>
                <span style={sectionTitle}>Online Interview</span>
            </Divider>
            <Row gutter={[12, 0]}>
                <Col xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Pilih Metode Interview *</label>
                        <Radio.Group value={interviewMethod} onChange={e => setInterviewMethod(e.target.value)}>
                            <Radio value="Zoom">Zoom</Radio>
                            <Radio value="Google Meet">Google Meet</Radio>
                            <Radio value="WhatsApp Video">WhatsApp Video</Radio>
                        </Radio.Group>
                    </div>
                </Col>
                <Col xl={8} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Tanggal Interview *</label>
                        <DatePicker
                            value={interviewDate}
                            onChange={d => setInterviewDate(d)}
                            disabledDate={disableSurveyDate}
                            format="DD-MM-YYYY"
                            style={{width: "100%"}}
                            placeholder="Pilih tanggal"
                        />
                    </div>
                </Col>
                <Col xl={8} xs={24}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Jam Interview *</label>
                        <TimePicker
                            value={interviewTime}
                            onChange={t => setInterviewTime(t)}
                            format="HH:mm"
                            minuteStep={30}
                            style={{width: "100%"}}
                            placeholder="Pilih jam"
                            disabledTime={() => ({disabledHours: () => [0,1,2,3,4,5,6,7,20,21,22,23]})}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderStep5 = () => {
        const submissionTypeLabel: any = {
            pengajuan_baru: "Pengajuan Baru (Non Driver)",
            driver_gsm_aktif: "Driver GSM Aktif",
            mitra_fleet_partner: "Mitra Fleet Partner"
        };

        const ReviewItem = ({label, value}: {label: string; value?: string | number | null}) => (
            value ? (
                <Row style={{marginBottom: 6}}>
                    <Col xs={12} sm={10}><Text type="secondary" style={{fontSize: 12}}>{label}</Text></Col>
                    <Col xs={12} sm={14}><Text strong style={{fontSize: 13}}>{value}</Text></Col>
                </Row>
            ) : null
        );

        return (
            <div>
                <Divider orientation="left" plain>
                    <span style={sectionTitle}>Ringkasan Pengajuan</span>
                </Divider>

                {/* Tipe Pengajuan */}
                <Card size="small" title={<span style={{color: "#384967"}}>Tipe Pengajuan</span>} style={{marginBottom: 16}}>
                    <ReviewItem label="Jenis Pengajuan" value={submissionTypeLabel[submissionType as string]}/>
                    <ReviewItem label="Referral ID" value={referralId || "-"}/>
                    {submissionType === "driver_gsm_aktif" && <>
                        <ReviewItem label="SAP ID" value={sapId}/>
                        <ReviewItem label="No HP GSM" value={gsmPhone}/>
                        <ReviewItem label="Area Operasional" value={operationalArea}/>
                        <ReviewItem label="Avg Income / Bulan" value={avgMonthlyIncome ? formatCurrency(avgMonthlyIncome) : ""}/>
                    </>}
                    {submissionType === "mitra_fleet_partner" && <>
                        <ReviewItem label="Nama Fleet" value={fleetName || "-"}/>
                        <ReviewItem label="Plat Nomor" value={vehiclePlate}/>
                        <ReviewItem label="Durasi Sewa" value={rentalDuration ? `${rentalDuration} bulan` : ""}/>
                        <ReviewItem label="Avg Setoran / Hari" value={avgDailyDeposit ? formatCurrency(avgDailyDeposit) : ""}/>
                    </>}
                    {submissionType === "pengajuan_baru" && <>
                        <ReviewItem label="Pernah Driver Online" value={hadDriverExperience === "ya" ? "Ya" : "Tidak"}/>
                        <ReviewItem label="Pengalaman Mengemudi" value={drivingExperienceYears !== null ? `${drivingExperienceYears} tahun` : ""}/>
                    </>}
                </Card>

                {/* Data Pribadi */}
                <Card size="small" title={<span style={{color: "#384967"}}>Data Pribadi</span>} style={{marginBottom: 16}}>
                    <ReviewItem label="Nama Lengkap" value={fullName?.toUpperCase()}/>
                    <ReviewItem label="NIK" value={nik}/>
                    <ReviewItem label="No KK" value={familyCardNumber}/>
                    <ReviewItem label="Tempat, Tgl Lahir" value={birthPlace && birthDate ? `${birthPlace}, ${birthDate.format("DD-MM-YYYY")}` : ""}/>
                    <ReviewItem label="Status Pernikahan" value={maritalStatus}/>
                    <ReviewItem label="No HP Utama" value={primaryPhone}/>
                    <ReviewItem label="Email" value={contactEmail || "-"}/>
                    <ReviewItem label="Alamat Domisili" value={domicileAddress}/>
                    <ReviewItem label="Kota" value={cityDistrict}/>
                    <ReviewItem label="Provinsi" value={provinceName}/>
                    <ReviewItem label="Status Rumah" value={homeOwnershipStatus}/>
                </Card>

                {/* Unit */}
                <Card size="small" title={<span style={{color: "#384967"}}>Unit Pilihan</span>} style={{marginBottom: 16}}>
                    <ReviewItem label="Kendaraan" value={selectedVehicleData?.name}/>
                    <ReviewItem label="Warna" value={selectedColor}/>
                    <ReviewItem label="OTR" value={selectedVehicleData?.otr}/>
                    <ReviewItem label="Uang Muka Siap" value={downPaymentReady ? formatCurrency(downPaymentReady) : ""}/>
                </Card>

                {/* Jadwal */}
                <Card size="small" title={<span style={{color: "#384967"}}>Jadwal</span>} style={{marginBottom: 16}}>
                    <ReviewItem label="Tgl Survey" value={surveyDate?.format("DD-MM-YYYY")}/>
                    <ReviewItem label="Jam Survey" value={surveyTime?.format("HH:mm")}/>
                    <ReviewItem label="Lokasi Survey" value={surveyLocation}/>
                    <ReviewItem label="Metode Interview" value={interviewMethod}/>
                    <ReviewItem label="Tgl Interview" value={interviewDate?.format("DD-MM-YYYY")}/>
                    <ReviewItem label="Jam Interview" value={interviewTime?.format("HH:mm")}/>
                </Card>

                {/* Persetujuan */}
                <Divider orientation="left" plain>
                    <span style={sectionTitle}>Persetujuan</span>
                </Divider>
                <div style={{marginBottom: 12}}>
                    <Checkbox
                        checked={agreeBI}
                        onChange={e => setAgreeBI(e.target.checked)}
                    >
                        <span style={{fontSize: 13}}>
                            Saya menyetujui proses <strong>BI Checking</strong> & analisa kredit dilakukan oleh tim Amarta Vinfast <span style={{color: "red"}}>*</span>
                        </span>
                    </Checkbox>
                </div>
                <div style={{marginBottom: 20}}>
                    <Checkbox
                        checked={agreeData}
                        onChange={e => setAgreeData(e.target.checked)}
                    >
                        <span style={{fontSize: 13}}>
                            Saya menyatakan bahwa seluruh data yang diisi adalah <strong>benar dan dapat dipertanggungjawabkan</strong> <span style={{color: "red"}}>*</span>
                        </span>
                    </Checkbox>
                </div>
            </div>
        );
    };

    // ── Step definitions ──────────────────────────────────────────────────────
    const steps = [
        {title: "Tipe Pengajuan", icon: <FileTextOutlined/>},
        {title: "KYC", icon: <UserOutlined/>},
        {title: "Pilih Unit", icon: <CarOutlined/>},
        {title: "Jadwal", icon: <CalendarOutlined/>},
        {title: "Review & Submit", icon: <CheckCircleOutlined/>},
    ];

    const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

    return (
        <>
            <PageHeader
                black={true}
                onAreaChangeProps={onAreaChange}
                areaCode={areaCode}
                isDesktop={isDesktop}
            />

            <Row className="pt-100">
                <Col xxl={{span: 16, offset: 4}} xl={{span: 16, offset: 4}} lg={{span: 20, offset: 2}} md={{span: 20, offset: 2}} sm={{span: 24}} xs={{span: 24}}>
                    <Breadcrumb className="pl-20">
                        <Breadcrumb.Item>Amarta {appName}</Breadcrumb.Item>
                        <Breadcrumb.Item>GSM Platform – Ajukan Kredit</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>

            <Row className="p-20 mt-20" style={{backgroundColor: "#fff"}}>
                <Col xxl={{span: 16, offset: 4}} xl={{span: 16, offset: 4}} lg={{span: 20, offset: 2}} md={{span: 20, offset: 2}} sm={{span: 24}} xs={{span: 24}}>

                    {/* Header */}
                    <div style={{textAlign: "center", marginBottom: 28}}>
                        <div style={{fontSize: "170%", fontWeight: 700, color: "#384967", marginBottom: 4}}>
                            GSM PLATFORM – FORM PENGAJUAN KREDIT
                        </div>
                        <div style={{fontSize: "100%", color: "gray"}}>
                            Isi data Anda dengan lengkap dan benar pada setiap langkah
                        </div>
                    </div>

                    {/* Progress */}
                    <div style={{marginBottom: 16}}>
                        <Progress
                            percent={progressPercent}
                            strokeColor="#384967"
                            trailColor="#e0e0e0"
                            strokeWidth={8}
                            format={() => `Step ${currentStep + 1} / ${TOTAL_STEPS}`}
                        />
                    </div>

                    {/* Steps indicator */}
                    <Steps
                        current={currentStep}
                        size="small"
                        style={{marginBottom: 28}}
                        responsive={true}
                    >
                        {steps.map((s, i) => (
                            <Step key={i} title={isDesktop ? s.title : ""} icon={s.icon}/>
                        ))}
                    </Steps>

                    {/* Step content */}
                    <div style={{minHeight: 300}}>
                        {stepContent[currentStep]()}
                    </div>

                    {/* Navigation */}
                    <Divider/>
                    <Row justify="space-between" align="middle">
                        <Col>
                            {currentStep > 0 && (
                                <Button
                                    onClick={handleBack}
                                    size="large"
                                    style={{minWidth: 120}}
                                >
                                    ← Kembali
                                </Button>
                            )}
                        </Col>
                        <Col>
                            {currentStep < TOTAL_STEPS - 1 ? (
                                <Button
                                    type="primary"
                                    onClick={handleNext}
                                    size="large"
                                    style={{
                                        backgroundColor: "#384967",
                                        borderColor: "#384967",
                                        minWidth: 140
                                    }}
                                >
                                    Selanjutnya →
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    loading={isSubmitting}
                                    size="large"
                                    disabled={!agreeBI || !agreeData}
                                    style={{
                                        backgroundColor: (!agreeBI || !agreeData) ? undefined : "#384967",
                                        borderColor: (!agreeBI || !agreeData) ? undefined : "#384967",
                                        color: (!agreeBI || !agreeData) ? undefined : "#fff",
                                        minWidth: 180,
                                        height: 48,
                                        fontSize: 15
                                    }}
                                >
                                    Kirim Pengajuan
                                </Button>
                            )}
                        </Col>
                    </Row>

                </Col>
            </Row>

            <div style={{minHeight: 100}}/>
            <FooterSection/>
        </>
    );
};

export default withRouter(FormGsmPlatform);
